import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBorrowings } from "@/hooks/useBorrowings";

export interface PlatformNotification {
  id: string;
  type: "borrowing_request" | "shipment" | "verification" | "system";
  title: string;
  message: string;
  date: string;
  unread: boolean;
  actionUrl?: string;
  actionLabel?: string;
}

export function usePlatformNotifications() {
  const { user, profile } = useAuth();
  const { data: borrowings = [] } = useBorrowings();

  const notifications = useMemo<PlatformNotification[]>(() => {
    if (!user) return [];

    const items: PlatformNotification[] = [];

    // 1. Solicitudes de préstamo recibidas pendientes de aprobación
    const pendingRequests = borrowings.filter(
      (b) => b.status === "requested" && b.owner_id === user.id
    );
    for (const req of pendingRequests) {
      items.push({
        id: `req-${req.id}`,
        type: "borrowing_request",
        title: "Nueva solicitud de alquiler",
        message: `Un vecino ha solicitado alquilar tu set ${req.sets?.set_name || ""}. Revisa los detalles para aceptar.`,
        date: req.created_at,
        unread: true,
        actionUrl: "/dashboard?tab=alquileres",
        actionLabel: "Ver solicitud",
      });
    }

    // 2. Préstamos donde eres prestatario y han sido aceptados (pendientes de entrega)
    const acceptedBorrowings = borrowings.filter(
      (b) => (b.status === "accepted" || b.status === "shipping") && b.borrower_id === user.id
    );
    for (const b of acceptedBorrowings) {
      items.push({
        id: `accepted-${b.id}`,
        type: "shipment",
        title: "¡Solicitud aprobada!",
        message: `El propietario ha aceptado tu solicitud para ${b.sets?.set_name || "el set"}. Se coordinará el envío.`,
        date: b.created_at,
        unread: true,
        actionUrl: "/dashboard?tab=alquileres",
        actionLabel: "Ver préstamo",
      });
    }

    // 3. Verificación de identidad requerida
    if (profile?.identity_required && !profile?.identity_verified) {
      items.push({
        id: "verification-needed",
        type: "verification",
        title: "Verifica tu identidad",
        message: "Para garantizar la seguridad de la comunidad, completa la verificación de identidad.",
        date: profile.created_at || new Date().toISOString(),
        unread: true,
        actionUrl: "/dashboard?tab=perfil&sub=verificacion",
        actionLabel: "Verificar ahora",
      });
    }

    // Ordenar por fecha descendente
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [user, profile, borrowings]);

  const unreadCount = notifications.filter((n) => n.unread).length;

  return {
    notifications,
    unreadCount,
  };
}
